import { NavLink, useParams } from "react-router-dom";
import { FileIcon } from "../FileIcon";
import useGqlClient from "@/src/hooks/useGqlClient";
import { links } from "@/src/utils/links";
import { FileType, useDataRoomFileQuery } from "@/src/graphql/generated";

function truncatePreview(text: string, max = 150): string {
  if (text.length <= max) {
    return text;
  }

  return text.slice(0, max).trimEnd();
}

export function SearchFileResultCard(props: {
  fileId: string;
  fileName?: string;
  fileType?: FileType;
  pageIndex?: number;
  previewText?: string;
  rectsOnPage?: number[][];
}) {
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();

  const fileQuery = useDataRoomFileQuery(
    client,
    { id: props.fileId },
    {
      enabled: props.fileId !== "",
      queryKey: ["SearchFileResultCard", { id: props.fileId }],
    },
  );

  const resolvedName = props.fileName || fileQuery.data?.dataRoomFile.name || props.fileId;
  const resolvedType = props.fileType || fileQuery.data?.dataRoomFile.fileType || FileType.Other;
  const folderId = fileQuery.data?.dataRoomFile.folderId;
  const hasLink = !!dealId && !!folderId;

  const body = (
    <div className="w-full max-w-full min-w-0 overflow-hidden rounded-md border border-concrete-200 bg-white px-3 py-2 hover:border-concrete-300 hover:shadow-sm transition-all">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2 min-w-0 overflow-hidden">
        <div className="pt-0.5">
          <FileIcon size="s" fileType={resolvedType} />
        </div>
        <div className="min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-x-2 min-w-0">
            <p className="text-xs font-semibold text-concrete-700 truncate min-w-0 max-w-full">
              {resolvedName}
            </p>
            {props.pageIndex !== undefined ? (
              <p className="text-xs text-gray-500 flex-shrink-0">
                Page {props.pageIndex + 1}
              </p>
            ) : null}
          </div>
          <div className="relative min-w-0 max-w-full overflow-hidden mt-1">
            <p className="italic min-w-0 max-w-full text-xs text-gray-600 whitespace-nowrap overflow-hidden pr-10">
              {truncatePreview(props.previewText || "")}
            </p>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
          </div>
        </div>
      </div>
    </div>
  );

  if (!hasLink) {
    return body;
  }

  return (
    <NavLink
      className="block w-full max-w-full min-w-0 overflow-hidden"
      to={links.fileLink(
        props.fileId,
        folderId,
        dealId,
        {
          pageIndex: props.pageIndex,
          rectsOnPage: props.rectsOnPage,
        },
      )}
    >
      {body}
    </NavLink>
  );
}
